import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {AllergenGroup, DisplayedAllergenGroup} from "app/models/menu.model";
import * as MenuSelectors from "app/reducers/menu.selectors";
import {Store} from "@ngrx/store";
import {Organization} from "app/models/organization.model";
import {MatDialog} from "@angular/material/dialog";
import {setAllergenGroups} from "app/reducers/menu.actions";
import {AllergenGroupApiService} from "app/services/menu-edit/allergen-group-api.service";
import {TranslateService} from "@ngx-translate/core";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";

@UntilDestroy()
@Component({
  selector: 'app-allergen-group',
  templateUrl: './allergen-group.component.html',
  styleUrls: ['./allergen-group.component.scss']
})
export class AllergenGroupComponent implements OnInit {
  @Input() allergenGroupIds: string[] | undefined;
  @Input() organization: Organization;
  @Input() menuSectionId: string;
  @Input() menuId: string;
  @Input() allowOverrides: boolean;
  @Input() readOnly: boolean = false;

  @Output() allergenGroupIdsChange = new EventEmitter<string[]>()
  initValues = {};

  allergenGroups: DisplayedAllergenGroup[] = [];

  allergenGroupIdSet: Set<string> = new Set();

  allergenGroupsMap: Map<string, DisplayedAllergenGroup>;
  originalAllergenGroupsMap: Map<string, DisplayedAllergenGroup>;

  constructor(
    private allergenGroupApiService: AllergenGroupApiService,
    public dialog: MatDialog,
    private translate: TranslateService,
    private store: Store,
  ) { }

  ngOnInit(): void {
    this.allergenGroupIdSet = new Set(this.allergenGroupIds);
    this.store.select(MenuSelectors.selectAllergenGroups)
      .pipe(untilDestroyed(this)).subscribe(allergenGroups => {
        this.allergenGroups = [...allergenGroups].sort((a, b) => a.name.localeCompare(b.name));
      });
    this.store.select(MenuSelectors.selectDictionaryAllergenGroup)
      .pipe(untilDestroyed(this)).subscribe(allergenGroups => {
        this.allergenGroupsMap = new Map<string, DisplayedAllergenGroup>();
        for (const kvp of allergenGroups) {
          this.allergenGroupsMap.set(kvp[0], kvp[1]);
        }
      });
    this.store.select(MenuSelectors.selectOriginalDictionaryAllergenGroup)
      .pipe(untilDestroyed(this)).subscribe(allergenGroups => {this.originalAllergenGroupsMap = allergenGroups});
    this.initValues['allergenGroups'] = this.allergenGroupIds?.map(x => x);
  }

  getAllergenName(allergenGroupId: string){
    return this.allergenGroupsMap?.get(allergenGroupId)?.name ?? allergenGroupId;
  }

  onRemoveAllergenGroupFromProduct(allergenGroupId: string) {
    if(!this.allowOverrides){
      return;
    }
    if(this.allergenGroupIdSet.delete(allergenGroupId)){
        this.allergenGroupIdsChange.emit([...this.allergenGroupIdSet]);
      }
  }

  onAllergenGroupPicked(id: string) {
    if(!this.allowOverrides){
      return;
    }
    if(!this.allergenGroupIdSet.has(id)) {
      this.allergenGroupIdSet.add(id);
      this.allergenGroupIdsChange.emit([...this.allergenGroupIdSet]);
    }
  }

  async onDeleteAllergenGroup(deletedId: string) {
    if(!this.allowOverrides){
      return;
    }
    if(confirm(this.translate.instant('MENU.ALLERGEN_GROUP.CONFIRM_DELETE'))){
      this.onRemoveAllergenGroupFromProduct(deletedId);
      await this.allergenGroupApiService.delete(deletedId);
      this.deleteFromStore(deletedId);
    }
  }

  deleteFromStore(deletedId: string) {
    if(!this.allowOverrides){
      return;
    }
    const filtered = [...this.allergenGroups.filter(allergen => allergen.id !== deletedId)];
    this.store.dispatch(setAllergenGroups(filtered));
  }

  isManuallyCreated(allergenGroupId: string) {
    const selected = this.allergenGroupsMap.get(allergenGroupId);
    return selected?.code == null || selected?.externalId == null;
  }
}
